import { AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import t from './asset.types';
import {
  getAssets,
  createAsset,
  updateAsset as svcUpdateAsset,
  deleteAsset as svcDeleteAsset,
  bulkDelete,
  duplicateAsset as svcDuplicateAsset,
  getAssetLocations as svcGetAssetLocations,
  getResumableUploads,
  getUploadResumeState,
  discardResumableUpload as svcDiscardResumableUpload,
  getAssetCompanyUsers,
  getAssetFolderOptions,
  getAssetProductOptions,
  searchAssets,
} from './asset.service';
import type { ResumableUploadRecord } from './asset.service';
import { uploadFileToS3 } from '../../../../services/s3-multipart-upload';
import { registerActiveUpload, isDetachedSignal } from '../../../../utils/active-uploads-registry';
import uploaderType from '../../../UploaderRedux/store/uploader.type';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import { ERROR_MESSAGE, getApiErrorKey, backendErrorMsg } from '../../../../Components/helper';
import type {
  Asset,
  AssetFilterPatch,
  AssetFilters,
  AssetLocationSection,
  AssetsState,
  CreateAssetPayload,
  UpdateAssetPayload,
  ViewMode,
  InfoPanelMode,
  AssetProductFilterSelection,
  AssetSearchPage,
} from '../types';
import { getAllBrands, getAllLicensor, getPlacementSubBrand } from '../../../../services/SystemMaster.services';
import { toMasterOptions, toProductOptions, toUserOptions } from '../assetOptionMappers';
import { logError } from '../../../../utils/logger';
import i18next from 'i18next';

interface ApiResponse<TData = unknown> {
  code?: number;
  message?: string;
  data?: TData;
}

interface ApiErrorData {
  errMsg?: string;
}

type AssetsThunk<TReturn = void> = ThunkAction<
  Promise<TReturn>,
  unknown,
  unknown,
  AnyAction
>;
type AssetsDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

export interface AssetsRootState {
  AssetLibraryAssets: AssetsState;
}

type MasterOptionsThunk = ThunkAction<Promise<void>, AssetsRootState, unknown, AnyAction>;

const getErrorMsg = (res: ApiResponse<unknown> | undefined): string => {
  const errMsg = (res?.data as ApiErrorData | undefined)?.errMsg;
  return errMsg || res?.message || ERROR_MESSAGE;
};

/** Shape of a rejected axios call, narrowed to the status the envelope was sent with. */
interface RejectedApiCall {
  response?: { status?: number };
}

/** Statuses the backend formats with a sentence written for the user (see sendCaughtError). */
const SURFACED_ERROR_STATUSES = [400, 404, 409];

/**
 * Message for a REJECTED API call. Axios rejects on every 4xx, so a business error such as the 409
 * raised when a product still uses the asset never reaches the resolved-envelope branch that
 * getErrorMsg covers, and the user was told the API connection had failed. Only the statuses the
 * server formats with a real sentence are surfaced; anything else carries a system message that
 * must not reach the UI.
 * @param {unknown} error the rejected axios error
 * @returns {string} a message that is safe to show the user
 */
const getCaughtErrorMsg = (error: unknown): string => {
  const httpStatus = (error as RejectedApiCall)?.response?.status;
  if (typeof httpStatus !== 'number' || !SURFACED_ERROR_STATUSES.includes(httpStatus)) return ERROR_MESSAGE;
  return getApiErrorKey(error) || ERROR_MESSAGE;
};

export const fetchAssets = (
  filters: AssetFilters = {},
  page = 1,
  limit = 16
): AssetsThunk => async (dispatch: AssetsDispatch): Promise<void> => {
  try {
    dispatch({ type: t.SET_LOADING, payload: true });
    const res: ApiResponse<{ assets: Asset[]; total: number; page: number; limit: number }> =
      await getAssets(filters, page, limit);
    if (res?.code === 200 && res.data) {
      dispatch({ type: t.FETCH_ASSETS_SUCCESS, payload: res.data });
    } else {
      dispatch({ type: t.SET_ERROR, payload: getErrorMsg(res) });
    }
  } catch (error) {
    logError('fetchAssets error:', error);
    dispatch({ type: t.SET_ERROR, payload: ERROR_MESSAGE });
    toasterMsg(ERROR_MESSAGE, 'danger');
  }
};

export const loadMoreAssets = (
  filters: AssetFilters = {},
  page = 1,
  limit = 16
): AssetsThunk => async (dispatch: AssetsDispatch): Promise<void> => {
  try {
    const res: ApiResponse<{ assets: Asset[]; total: number; page: number; limit: number }> =
      await getAssets(filters, page, limit);
    if (res?.code === 200 && res.data) {
      dispatch({ type: t.LOAD_MORE_ASSETS_SUCCESS, payload: res.data });
    }
  } catch (error) {
    logError('loadMoreAssets error:', error);
  }
};

export const uploadAsset = (
  payload: CreateAssetPayload
): AssetsThunk<Asset | null> => async (dispatch: AssetsDispatch): Promise<Asset | null> => {
  try {
    const res: ApiResponse<Asset & ApiErrorData> = await createAsset(payload);
    if (res?.code === 200 || res?.code === 201) {
      const asset = res.data as Asset;
      dispatch({ type: t.UPLOAD_ASSET_SUCCESS, payload: asset });
      return asset;
    }
    toasterMsg(getErrorMsg(res), 'danger');
    return null;
  } catch (error) {
    logError('uploadAsset error:', error);
    toasterMsg(ERROR_MESSAGE, 'danger');
    return null;
  }
};

export const updateAsset = (
  assetId: string,
  payload: UpdateAssetPayload
): AssetsThunk<boolean> => async (dispatch: AssetsDispatch): Promise<boolean> => {
  try {
    const res: ApiResponse<Asset & ApiErrorData> = await svcUpdateAsset(assetId, payload);
    if (res?.code === 200) {
      dispatch({ type: t.UPDATE_ASSET_SUCCESS, payload: res.data as Asset });
      toasterMsg('Asset saved successfully', 'success');
      return true;
    }
    toasterMsg(getErrorMsg(res), 'danger');
    return false;
  } catch (error) {
    logError('updateAsset error:', error);
    toasterMsg(ERROR_MESSAGE, 'danger');
    return false;
  }
};

export const deleteAsset = (
  assetId: string
): AssetsThunk<boolean> => async (dispatch: AssetsDispatch): Promise<boolean> => {
  try {
    const res: ApiResponse<ApiErrorData> = await svcDeleteAsset(assetId);
    if (res?.code === 200) {
      dispatch({ type: t.DELETE_ASSET_SUCCESS, payload: { assetId } });
      toasterMsg('Asset deleted successfully', 'success');
      return true;
    }
    toasterMsg(getErrorMsg(res), 'danger');
    return false;
  } catch (error) {
    logError('deleteAsset error:', error);
    toasterMsg(getCaughtErrorMsg(error), 'danger');
    return false;
  }
};

export const bulkDeleteAssets = (
  assetIds: string[]
): AssetsThunk<boolean> => async (dispatch: AssetsDispatch): Promise<boolean> => {
  try {
    const res: ApiResponse<ApiErrorData> = await bulkDelete(assetIds);
    if (res?.code === 200) {
      dispatch({ type: t.BULK_DELETE_SUCCESS, payload: { assetIds } });
      toasterMsg(`${assetIds.length} asset${assetIds.length !== 1 ? 's' : ''} deleted successfully`, 'success');
      return true;
    }
    toasterMsg(getErrorMsg(res), 'danger');
    return false;
  } catch (error) {
    logError('bulkDeleteAssets error:', error);
    toasterMsg(getCaughtErrorMsg(error), 'danger');
    return false;
  }
};

export const duplicateAsset = (
  assetId: string
): AssetsThunk<boolean> => async (dispatch: AssetsDispatch): Promise<boolean> => {
  try {
    const res: ApiResponse<Asset & ApiErrorData> = await svcDuplicateAsset(assetId);
    if (res?.code === 200 || res?.code === 201) {
      dispatch({ type: t.DUPLICATE_ASSET_SUCCESS, payload: res.data as Asset });
      toasterMsg('Asset duplicated successfully', 'success');
      return true;
    }
    toasterMsg(getErrorMsg(res), 'danger');
    return false;
  } catch (error) {
    logError('duplicateAsset error:', error);
    toasterMsg(ERROR_MESSAGE, 'danger');
    return false;
  }
};

/** Fetch the modules/products where the given asset is referenced, for the Locations panel. */
export const fetchAssetLocations = (
  assetId: string
): AssetsThunk => async (dispatch: AssetsDispatch): Promise<void> => {
  try {
    dispatch({ type: t.FETCH_ASSET_LOCATIONS_START });
    const res: ApiResponse<{ sections: AssetLocationSection[] }> = await svcGetAssetLocations(assetId);
    if (res?.code === 200 && res.data) {
      dispatch({ type: t.FETCH_ASSET_LOCATIONS_SUCCESS, payload: res.data.sections });
    } else {
      dispatch({ type: t.FETCH_ASSET_LOCATIONS_ERROR, payload: getErrorMsg(res) });
    }
  } catch (error) {
    logError('fetchAssetLocations error:', error);
    dispatch({ type: t.FETCH_ASSET_LOCATIONS_ERROR, payload: ERROR_MESSAGE });
  }
};

/**
 * Fetches the company-wide master lists shared by the assets grid, the brand/licensor filter and
 * every mounted metadata form — brands, sub brands, licensors, products, users and folders — and
 * caches them in the store.
 *
 * The lists are read-mostly reference data, so the fetch runs once per session: a call made while
 * a fetch is in flight, or after one resolved, returns without hitting the network. That is what
 * keeps a panel or a per-file metadata form from re-requesting the same six endpoints on every
 * mount. Individual failures degrade to an empty list rather than failing the whole batch, and a
 * failed batch leaves `loaded` false so the next mount retries.
 */
export const fetchAssetMasterOptions =
  (): MasterOptionsThunk =>
  async (dispatch: AssetsDispatch, getState: () => AssetsRootState): Promise<void> => {
    const { loading, loaded } = getState().AssetLibraryAssets.masterOptions;
    if (loading || loaded) return;

    dispatch({ type: t.FETCH_MASTER_OPTIONS_START });
    try {
      const settled = await Promise.allSettled([
        getAllBrands({ isActive: true }),
        getPlacementSubBrand(),
        getAllLicensor({ isActive: true }),
        getAssetProductOptions(),
        getAssetCompanyUsers(),
        getAssetFolderOptions(),
      ]);
      const [brandRes, subBrandRes, licensorRes, productRes, userRes, folderRes] = settled;

      if (settled.every((result) => result.status === 'rejected')) {
        dispatch({ type: t.FETCH_MASTER_OPTIONS_ERROR });
        return;
      }

      dispatch({
        type: t.FETCH_MASTER_OPTIONS_SUCCESS,
        payload: {
          brands: brandRes.status === 'fulfilled' ? toMasterOptions(brandRes.value?.data) : [],
          subBrands: subBrandRes.status === 'fulfilled' ? toMasterOptions(subBrandRes.value?.data) : [],
          licensors: licensorRes.status === 'fulfilled' ? toMasterOptions(licensorRes.value?.data) : [],
          products: productRes.status === 'fulfilled' && productRes.value?.code === 200 ? toProductOptions(productRes.value.data) : [],
          users: userRes.status === 'fulfilled' && userRes.value?.code === 200 ? toUserOptions(userRes.value.data) : [],
          folders: folderRes.status === 'fulfilled' && folderRes.value?.code === 200 ? toMasterOptions(folderRes.value.data?.all) : [],
        },
      });
    } catch (error) {
      logError('fetchAssetMasterOptions error:', error);
      dispatch({ type: t.FETCH_MASTER_OPTIONS_ERROR });
    }
  };

export const setFilter = (filter: AssetFilterPatch): AnyAction => ({
  type: t.SET_FILTER,
  payload: filter,
});

export const resetFilters = (): AnyAction => ({ type: t.RESET_FILTERS });

export const setPage = (page: number): AnyAction => ({
  type: t.SET_PAGE,
  payload: page,
});

export const setViewMode = (viewMode: ViewMode): AnyAction => ({
  type: t.SET_VIEW_MODE,
  payload: viewMode,
});

export const toggleSelectAsset = (assetId: string): AnyAction => ({
  type: t.TOGGLE_SELECT_ASSET,
  payload: assetId,
});

export const selectAllAssets = (assetIds: string[]): AnyAction => ({
  type: t.SELECT_ALL_ASSETS,
  payload: assetIds,
});

export const clearSelection = (): AnyAction => ({ type: t.CLEAR_SELECTION });

export const setActiveAsset = (assetId: string | null): AnyAction => ({
  type: t.SET_ACTIVE_ASSET,
  payload: assetId,
});

export const setUploadPanelOpen = (open: boolean): AnyAction => ({
  type: t.SET_UPLOAD_PANEL_OPEN,
  payload: open,
});

export const setInfoPanelOpen = (open: boolean): AnyAction => ({
  type: t.SET_INFO_PANEL_OPEN,
  payload: open,
});

export const setInfoPanelMode = (mode: InfoPanelMode): AnyAction => ({
  type: t.SET_INFO_PANEL_MODE,
  payload: mode,
});

export const setDownloadPanelOpen = (open: boolean): AnyAction => ({
  type: t.SET_DOWNLOAD_PANEL_OPEN,
  payload: open,
});

export const setSharePanelOpen = (open: boolean): AnyAction => ({
  type: t.SET_SHARE_PANEL_OPEN,
  payload: open,
});

export const setLocationsPanelOpen = (open: boolean): AnyAction => ({
  type: t.SET_LOCATIONS_PANEL_OPEN,
  payload: open,
});

export const setCommentsPanelOpen = (open: boolean): AnyAction => ({
  type: t.SET_COMMENTS_PANEL_OPEN,
  payload: open,
});

export const setInfoDetailsPanelOpen = (open: boolean): AnyAction => ({
  type: t.SET_INFO_DETAILS_PANEL_OPEN,
  payload: open,
});

/** Module key stamped on every asset-library pending-upload record. */
export const ASSET_UPLOAD_MODULE = 'asset-library';

/**
 * Pushes resumable-upload records into the floating uploader widget as `resumable` rows, so the
 * user is offered Resume / Discard wherever they are in the app.
 * @param dispatch - Redux dispatch.
 * @param records - Resumable-upload records from the API.
 */
const pushResumableRows = (dispatch: AssetsDispatch, records: ResumableUploadRecord[]): void => {
  records.forEach((record) => {
    dispatch({
      type: uploaderType.ADD_FILES_IN_QUEUE_UPLOADER,
      payload: {
        [record.taskId]: {
          taskId: record.taskId,
          fileName: record.fileName,
          fileSize: record.fileSize,
          module: record.module,
          percentage: 0,
          completed: false,
          cancellable: false,
          status: 'resumable',
        },
      },
    });
  });
};

/**
 * Fetches every unfinished upload the logged-in user owns and shows them in the floating widget.
 * Called when the widget mounts, so a resume is offered after a relogin, a crash or a tab close.
 */
export const fetchResumableUploads =
  (): AssetsThunk<ResumableUploadRecord[]> =>
  async (dispatch: AssetsDispatch): Promise<ResumableUploadRecord[]> => {
    try {
      const res = await getResumableUploads();
      const resumable = res?.code === 200 && Array.isArray(res.data) ? res.data : [];
      pushResumableRows(dispatch, resumable);
      return resumable;
    } catch (error) {
      logError('fetchResumableUploads error:', error);
      return [];
    }
  };

/**
 * Discards an unfinished upload: aborts it in S3, clears the resume record, and drops the row from
 * the floating widget. The row is removed even when the API call fails, so a stale row can never
 * strand the widget.
 */
export const discardResumableUpload =
  (taskId: string): AssetsThunk<void> =>
  async (dispatch: AssetsDispatch): Promise<void> => {
    try {
      await svcDiscardResumableUpload(taskId);
    } catch (error) {
      logError('discardResumableUpload error:', error);
    } finally {
      dispatch({ type: uploaderType.REMOVE_TASK_FROM_LIST, payload: taskId });
    }
  };

/**
 * Resumes a previously-started asset upload.
 *
 * The browser cannot keep the file bytes across a session, so the user re-picks the file and it is
 * verified against the pending record by name and size before a single byte is sent — a different
 * file would corrupt the object, since the already-stored parts are kept and only the gaps are
 * re-uploaded.
 *
 * User-entered metadata is deliberately not persisted with the resume record, so the finished
 * upload becomes an asset titled after its file name; the user edits the rest through the normal
 * asset edit flow.
 * @param record - The resumable row the user clicked Resume on.
 * @param repickedFile - The file the user re-selected.
 * @param onComplete - Called after the asset is created, so the caller can refresh its list.
 * @returns True when the upload completed and the asset was created.
 */
export const resumeAssetUpload =
  (record: ResumableUploadRecord, repickedFile: File, onComplete?: () => void): AssetsThunk<boolean> =>
  async (dispatch: AssetsDispatch): Promise<boolean> => {
    if (record.module !== ASSET_UPLOAD_MODULE) {
      return false;
    }
    if (repickedFile.name !== record.fileName || repickedFile.size !== record.fileSize) {
      toasterMsg('That is a different file. Pick the exact file this upload was started with.', 'danger');
      return false;
    }

    const stateRes = await getUploadResumeState(record.taskId).catch((error: unknown) => {
      logError('resumeAssetUpload resume-state error:', error);
      return null;
    });
    if (stateRes?.code !== 200 || !stateRes.data?.uploadId) {
      toasterMsg('This upload has expired and can no longer be resumed.', 'danger');
      dispatch({ type: uploaderType.REMOVE_TASK_FROM_LIST, payload: record.taskId });
      return false;
    }

    const { uploadId, objectKey, partSize, partCount, uploadedPartNumbers, section } = stateRes.data;
    const controller = new AbortController();
    const unregisterUpload = registerActiveUpload(record.taskId, controller);
    const removeRow = (): void => {
      unregisterUpload();
      dispatch({ type: uploaderType.REMOVE_TASK_FROM_LIST, payload: record.taskId });
    };

    dispatch({
      type: uploaderType.ADD_FILES_IN_QUEUE_UPLOADER,
      payload: {
        [record.taskId]: {
          taskId: record.taskId,
          fileName: record.fileName,
          percentage: 0,
          completed: false,
          cancellable: true,
          status: 'uploading',
        },
      },
    });

    try {
      await uploadFileToS3(repickedFile, objectKey, {
        signal: controller.signal,
        taskId: record.taskId,
        keepUploadOnAbort: () => isDetachedSignal(controller.signal),
        onProgress: (percentage: number) =>
          dispatch({
            type: uploaderType.UPLOADFILE_PERCENTAGE_STATUS,
            payload: { taskId: record.taskId, filePercentage: percentage },
          }),
        resume: { uploadId, partSize, partCount, uploadedPartNumbers },
        ...(section ? { section } : {}),
      });

      /**
       * assetCategory and fileType are stored on the pending record precisely because they cannot
       * be re-derived reliably after the fact; the fallbacks only cover a record written before
       * those fields existed, since the create call rejects an empty fileType.
       */
      const createdAsset = await dispatch(
        uploadAsset({
          title: record.fileName,
          fileUrl: objectKey,
          fileType: record.fileType || repickedFile.type || 'application/octet-stream',
          assetCategory: (record.assetCategory as CreateAssetPayload['assetCategory']) || 'document',
          size: record.fileSize,
          isActive: true,
        }),
      );

      removeRow();
      if (!createdAsset) return false;
      toasterMsg(`${record.fileName} finished uploading. Add its details from the asset.`, 'success');
      if (onComplete) onComplete();
      return true;
    } catch (error) {
      if (controller.signal.aborted && isDetachedSignal(controller.signal)) {
        unregisterUpload();
        return false;
      }
      if (!controller.signal.aborted) {
        logError('resumeAssetUpload error:', error);
        toasterMsg(`${record.fileName} could not be uploaded.`, 'danger');
      }
      removeRow();
      return false;
    }
  };

/**
 * Loads one cursor page of assets that belong to products matching the product filter.
 * @param productFilter - Products page filter selection
 * @param filters - Asset filters from the filter bar
 * @param cursor - Cursor the page is requested with; null for the first page
 * @returns Resolves once the page is stored or the failure is shown
 */
export const fetchAssetsPage =
  (productFilter: AssetProductFilterSelection, filters: AssetFilters, cursor: string | null): AssetsThunk =>
  async (dispatch: AssetsDispatch): Promise<void> => {
    try {
      dispatch({ type: t.SET_LOADING, payload: true });
      const res: ApiResponse<AssetSearchPage> = await searchAssets({ productFilter, assetFilter: filters, cursor });
      if (res?.code === 200 && res.data) {
        dispatch({ type: t.FETCH_ASSETS_PAGE_SUCCESS, payload: res.data });
      } else {
        dispatch({ type: t.SET_ERROR, payload: ERROR_MESSAGE });
      }
    } catch (error) {
      logError('fetchAssetsPage error:', error);
      const message = backendErrorMsg(translateAssetKey, getCaughtErrorMsg(error));
      dispatch({ type: t.SET_ERROR, payload: message });
      toasterMsg(message, 'danger');
    }
  };

export const setProductFilter = (productFilter: AssetProductFilterSelection): AnyAction => ({
  type: t.SET_PRODUCT_FILTER,
  payload: productFilter,
});

export const nextPage = (): AnyAction => ({ type: t.NEXT_PAGE });

export const prevPage = (): AnyAction => ({ type: t.PREV_PAGE });

/**
 * Translates a backend error key in the Asset Library namespace.
 * @param key - Key returned by the server
 * @returns The translation, or the key itself when none exists
 */
function translateAssetKey(key: string): string {
  return i18next.t(key, { ns: ASSET_LIBRARY_NAMESPACE });
}

const ASSET_LIBRARY_NAMESPACE = 'assetLibraryAssets';
